import {AtaDetalhe, AtaResumo, ItemAta} from '../types'
import {ESFERA_LABEL, fmtBRL, fmtDate, itemAderenteLocal, seloInfo} from '../ui'

interface Props {
    ata: AtaDetalhe | null
    resumo: AtaResumo | null
    /** Busca original do usuário — destaca o(s) item(ns) RECOMENDADO(s) na tabela
     * (sinal `aderente` do backend + fallback local). Opcional (testes/históricos). */
    termoBuscado?: string
    onVoltar: () => void
    onAvaliar: () => void
}

const SALDO_LABEL: Record<string, string> = {
    DISPONIVEL: 'Disponível', ESGOTADO: 'Esgotado (limite 200%)', INDETERMINADO: 'Indeterminado',
}

interface Detentor {
    nome: string
    cnpj: string
}

/** Formata um CNPJ de 14 dígitos como 00.000.000/0000-00. Valor com outro tamanho
 * volta como veio (não força máscara em dado inesperado). */
function fmtCnpj(v: string | null | undefined): string {
    const d = (v || '').replace(/\D/g, '')
    if (d.length !== 14) return (v || '').trim()
    return d.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5')
}

/** Detentores DISTINTOS da ata — a(s) empresa(s) que registrou os preços (quem o
 * gestor contrata na adesão). Deduplica por CNPJ (cai no nome quando o PNCP não
 * trouxe o CNPJ). Itens sem fornecedor são ignorados; a ausência total vira
 * mensagem honesta na tela. */
function detentoresDaAta(itens: ItemAta[]): Detentor[] {
    const map = new Map<string, Detentor>()
    for (const it of itens) {
        const nome = (it.fornecedor_nome || '').trim()
        const cnpj = (it.fornecedor_cnpj || '').trim()
        if (!nome && !cnpj) continue
        const key = cnpj.replace(/\D/g, '') || nome.toUpperCase()
        if (!map.has(key)) map.set(key, {nome, cnpj})
    }
    return Array.from(map.values())
}

export default function DetalheAta({ata, resumo, termoBuscado = '', onVoltar, onAvaliar}: Props) {
    // Navegação otimista: entra na página com o RESUMO (já conhecido da lista) e
    // carrega os itens/saldo/documentos do PNCP aqui dentro, sem prender a tela.
    const base = ata || resumo
    if (!base) {
        return (
            <div className="page-content">
                <div className="lg-empty"><div className="dica-spinner" aria-hidden="true" />
                    <div className="lg-empty__title">Carregando ata…</div></div>
            </div>
        )
    }
    const carregandoItens = !ata // resumo presente, detalhe completo ainda a caminho

    const selo = seloInfo(base.selo, base.selo_rotulo)
    const podeAvaliar = selo.key !== 'vedada' && selo.key !== 'vencida'
    const sv = base.status_vigencia
    const saldoBs = ata?.saldo_status || ''
    const saldoCls = saldoBs === 'DISPONIVEL' ? 'callout--success' : saldoBs === 'ESGOTADO' ? 'callout--danger' : 'callout--warn'

    // Detentor(es) da ata — só computável com os itens carregados (não vem no resumo).
    const detentores = ata ? detentoresDaAta(ata.itens) : []
    const multiDetentor = detentores.length > 1
    const detentorUnico = detentores[0] || null

    return (
        <div>
            <header className="page-header">
                <div className="crumb">
                    <a onClick={onVoltar}>Resultados</a>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
                    <span>Detalhe da ata</span>
                </div>
                <div className="header-row">
                    <h1 className="lg-h2" style={{maxWidth: 760}}>{base.objeto}</h1>
                    <span className={`selo ${selo.cls}`}>{selo.label}</span>
                </div>
                <p className="lg-text-muted" style={{marginBottom: 0}}>
                    {base.orgao} · {ESFERA_LABEL[base.esfera_codigo] || base.esfera} · {base.uf} · Nº controle PNCP {base.numero_controle_pncp}
                </p>
            </header>

            <div className="page-content" style={{maxWidth: 1140}}>
                <div className="detail-grid">
                    <div>
                        {ata && (
                            <div style={{
                                display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18,
                                padding: '12px 16px', background: 'var(--surface-sunken)',
                                border: '1px solid var(--border-subtle)', borderLeft: '3px solid var(--brand-navy)',
                                borderRadius: 'var(--radius-lg)',
                            }}>
                                <span aria-hidden="true" style={{color: 'var(--brand-navy)', display: 'flex', flexShrink: 0}}>
                                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z"/><path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2"/><path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2"/><path d="M10 6h4"/><path d="M10 10h4"/><path d="M10 14h4"/><path d="M10 18h4"/>
                                    </svg>
                                </span>
                                <div>
                                    <div style={{fontSize: 'var(--fs-xs)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-wide)', color: 'var(--text-tertiary)', fontWeight: 'var(--fw-semibold)' as any}}>
                                        {multiDetentor ? 'Detentores da ata' : 'Detentor da ata'}
                                    </div>
                                    {multiDetentor ? (
                                        <div style={{fontSize: 'var(--fs-base)', fontWeight: 'var(--fw-semibold)' as any, color: 'var(--brand-navy)'}}>
                                            {detentores.length} fornecedores — veja o detentor de cada item abaixo
                                        </div>
                                    ) : detentorUnico && (detentorUnico.nome || detentorUnico.cnpj) ? (
                                        <div style={{fontSize: 'var(--fs-base)', fontWeight: 'var(--fw-semibold)' as any, color: 'var(--brand-navy)'}}>
                                            {detentorUnico.nome || 'Não informado no PNCP'}
                                            {detentorUnico.cnpj && (
                                                <span style={{fontWeight: 'var(--fw-regular)' as any, color: 'var(--text-secondary)'}}> · CNPJ {fmtCnpj(detentorUnico.cnpj)}</span>
                                            )}
                                        </div>
                                    ) : (
                                        <div style={{fontSize: 'var(--fs-sm)', color: 'var(--text-tertiary)'}}>Não informado no PNCP</div>
                                    )}
                                    <div style={{fontSize: 'var(--fs-xs)', color: 'var(--text-tertiary)', marginTop: 2}}>
                                        Empresa que registrou os preços — é quem você contrata na adesão.
                                    </div>
                                </div>
                            </div>
                        )}
                        <div className="detail-panel">
                            <div className="detail-panel__title">Itens e preços registrados</div>
                            {carregandoItens ? (
                                <div className="lg-empty" style={{padding: '28px 16px'}}>
                                    <div className="dica-spinner" aria-hidden="true" />
                                    <div className="lg-empty__description" style={{marginTop: 6}}>Buscando itens e preços registrados no PNCP…</div>
                                </div>
                            ) : (
                                <>
                                    <div style={{overflowX: 'auto'}}>
                                        <table className="doc-table">
                                            <thead><tr>
                                                <th>#</th><th>Descrição</th><th>Un.</th>
                                                <th style={{textAlign: 'right'}}>Qtd. registrada</th>
                                                <th style={{textAlign: 'right'}}>Preço registrado</th>
                                                <th style={{textAlign: 'right'}}>Limite 50% (§3º)</th>
                                            </tr></thead>
                                            <tbody>
                                                {ata!.itens.length === 0 && (
                                                    <tr><td colSpan={6} style={{color: 'var(--text-tertiary)', padding: 14}}>
                                                        Sem itens cadastrados para esta ata.</td></tr>
                                                )}
                                                {ata!.itens.map((it) => {
                                                    // Detentor único já aparece no destaque do topo; aqui a metinha fica só
                                                    // com a marca. Em ata multi-fornecedor, o detentor vem rotulado por item.
                                                    const marca = (it.marca || '').trim()
                                                    const itNome = (it.fornecedor_nome || '').trim()
                                                    const itCnpj = (it.fornecedor_cnpj || '').trim()
                                                    // Item RECOMENDADO para a busca (backend ?termo= + fallback
                                                    // local) — realce visual, nada é bloqueado; pode haver vários.
                                                    const recomendado = Boolean(it.aderente) || itemAderenteLocal(termoBuscado, it.descricao)
                                                    return (
                                                        <tr key={it.numero_item} style={recomendado ? {background: 'var(--feedback-info-soft)'} : undefined}>
                                                            <td style={recomendado ? {borderLeft: '3px solid var(--feedback-info)'} : undefined}>{it.numero_item}</td>
                                                            <td>{it.descricao}
                                                                {recomendado && (
                                                                    <span
                                                                        className="lg-badge lg-badge--info"
                                                                        title={`Recomendado para a busca "${(termoBuscado || '').trim()}"`}
                                                                        style={{marginLeft: 8, verticalAlign: 'middle'}}
                                                                    >
                                                                        Recomendado para a sua busca
                                                                    </span>
                                                                )}
                                                                {marca && <><br/><span style={{color: 'var(--text-tertiary)', fontSize: 'var(--fs-xs)'}}>{marca}</span></>}
                                                                {multiDetentor && (
                                                                    <><br/><span style={{fontSize: 'var(--fs-xs)', color: 'var(--text-secondary)'}}>
                                                                        Detentor: {itNome ? <strong style={{color: 'var(--brand-navy)'}}>{itNome}</strong> : 'não informado no PNCP'}
                                                                        {itCnpj && <> · CNPJ {fmtCnpj(itCnpj)}</>}
                                                                    </span></>
                                                                )}
                                                            </td>
                                                            <td>{it.unidade}</td>
                                                            <td style={{textAlign: 'right'}}>{it.qtd_registrada.toLocaleString('pt-BR')}</td>
                                                            <td style={{textAlign: 'right', fontWeight: 'var(--fw-semibold)' as any}}>
                                                                {fmtBRL(it.valor_unitario)}
                                                                {it.valor_unitario != null && (
                                                                    <><br/><span style={{fontWeight: 'var(--fw-regular)' as any, fontSize: 'var(--fs-xs)', color: it.valor_estimado ? 'var(--text-tertiary)' : 'var(--feedback-success-strong)'}}>
                                                                        {it.valor_estimado ? 'estimado' : 'registrado'}</span></>
                                                                )}
                                                            </td>
                                                            <td style={{textAlign: 'right', color: 'var(--feedback-info-strong)'}}>{Math.floor(it.qtd_registrada * 0.5).toLocaleString('pt-BR')}</td>
                                                        </tr>
                                                    )
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                    {(() => {
                                        const total = ata!.itens.reduce((s, it) => s + (it.valor_unitario || 0) * (it.qtd_registrada || 0), 0)
                                        const algumEstimado = ata!.itens.some((it) => it.valor_estimado && it.valor_unitario != null)
                                        return total > 0 ? (
                                            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10, padding: '10px 12px', background: 'var(--surface-sunken)', borderRadius: 'var(--radius-md)'}}>
                                                <span style={{fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)'}}>
                                                    Valor total registrado na ata{algumEstimado ? ' (alguns itens estimados)' : ''}
                                                </span>
                                                <strong style={{fontSize: 'var(--fs-lg)', color: 'var(--brand-navy)'}}>{fmtBRL(total)}</strong>
                                            </div>
                                        ) : null
                                    })()}
                                    <p style={{fontSize: 'var(--fs-xs)', color: 'var(--text-tertiary)', marginTop: 8}}>
                                        Itens e valores vêm da <strong>compra de origem</strong>. A última coluna é o teto individual (50%) que o aderente pode contratar por item. O total acima é o valor cheio da ata — sua adesão será uma fração (até 50% por item).
                                    </p>
                                </>
                            )}
                        </div>

                        <div className="detail-panel">
                            <div className="detail-panel__title">Saldo de adesões — limite coletivo de 200% (art. 86, §4º)</div>
                            {carregandoItens ? (
                                <div className="callout callout--warn" style={{margin: 0}}>
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                                    <div><span className="callout__title">Confirmando saldo…</span>
                                        Carregando o status do limite coletivo no PNCP.</div>
                                </div>
                            ) : (
                                <div className={`callout ${saldoCls}`} style={{margin: 0}}>
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                                    <div>
                                        <span className="callout__title">Saldo coletivo: {SALDO_LABEL[saldoBs] || saldoBs}</span>
                                        Confirme o saldo do limite de 200% diretamente com o órgão gerenciador antes de aderir. Nunca presuma saldo disponível.
                                    </div>
                                </div>
                            )}
                        </div>

                        {ata && ata.documentos.length > 0 && (
                            <div className="detail-panel">
                                <div className="detail-panel__title">Documentos no PNCP</div>
                                {ata.documentos.map((d, i) => (
                                    <a key={i} className="doc-link" href={d.url} target="_blank" rel="noopener noreferrer">
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                                        {d.tipo || 'Documento'}
                                    </a>
                                ))}
                            </div>
                        )}
                    </div>

                    <div>
                        <div className="aside-sticky">
                            <div className="detail-panel">
                                <div className="detail-panel__title">Dados da ata</div>
                                <dl className="kv">
                                    <dt>Órgão gerenciador</dt><dd>{base.orgao}</dd>
                                    <dt>Esfera</dt><dd>{ESFERA_LABEL[base.esfera_codigo] || base.esfera}</dd>
                                    <dt>UF</dt><dd>{base.uf || '—'}</dd>
                                    <dt>Modalidade</dt><dd>{base.modalidade || '—'}</dd>
                                    <dt>Vigência</dt><dd>{fmtDate(base.vigencia_inicio)} – {fmtDate(base.vigencia_fim)}</dd>
                                    <dt>Status</dt><dd>{sv === 'VIGENTE' ? 'Vigente' : sv === 'A_VENCER' ? 'A vencer' : sv === 'VENCIDA' ? 'Vencida' : 'Cancelada'}</dd>
                                    <dt>Prevê adesão?</dt><dd>{base.permite_adesao === 'SIM' ? 'Sim' : base.permite_adesao === 'NAO' ? 'Não' : 'A confirmar'}</dd>
                                </dl>
                            </div>
                            <button className="lg-btn lg-btn--brand lg-btn--block lg-btn--lg" disabled={!podeAvaliar} onClick={onAvaliar}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                                {podeAvaliar ? 'Gerar justificativa de adesão' : (selo.key === 'vedada' ? 'Adesão vedada por esfera' : 'Ata fora de vigência')}
                            </button>
                            {!podeAvaliar && (
                                <p style={{fontSize: 'var(--fs-xs)', color: 'var(--text-tertiary)', textAlign: 'center', marginTop: 8}}>
                                    {selo.key === 'vedada' ? 'Vedação do art. 86, §8º para o aderente federal.' : 'A adesão só pode ocorrer com ata vigente (art. 84).'}
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
